import { motion } from "framer-motion";
import { SupplierTariff, formatEur } from "@/lib/index";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

interface SupplierDataPanelProps {
  p1Tariffs: SupplierTariff[];
  p2Tariffs: SupplierTariff[];
  onUpdateTariff: (supplier: 'p1' | 'p2', id: string, field: keyof SupplierTariff, value: number) => void;
}

function calculateInvoice(tariff: number, discount: number): number {
  return tariff * (1 - discount / 100);
}

function calculateRappelEur(tariff: number, rappel: number): number {
  return tariff * (rappel / 100);
}

function calculateFinalPrice(invoice: number, rappelEur: number, rappelDiscount: number): number {
  return invoice - rappelEur * (1 - rappelDiscount / 100);
}

function TariffTable({ tariffs, supplier, onUpdate }: { tariffs: SupplierTariff[]; supplier: 'p1' | 'p2'; onUpdate: (id: string, field: keyof SupplierTariff, value: number) => void }) {
  const totals = tariffs.reduce(
    (acc, t) => {
      const invoice = calculateInvoice(t.tariff, t.invoiceDiscount);
      const rappelEur = calculateRappelEur(t.tariff, t.rappel);
      const finalPrice = calculateFinalPrice(invoice, rappelEur, t.rappelDiscount);
      return {
        tariff: acc.tariff + t.tariff,
        invoice: acc.invoice + invoice,
        rappelEur: acc.rappelEur + rappelEur,
        finalPrice: acc.finalPrice + finalPrice
      };
    },
    { tariff: 0, invoice: 0, rappelEur: 0, finalPrice: 0 }
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left p-3 text-sm font-semibold text-foreground">FORMATO</th>
            <th className="text-right p-3 text-sm font-semibold text-foreground">TARIFA</th>
            <th className="text-right p-3 text-sm font-semibold text-foreground">DESC.FACTURA%</th>
            <th className="text-right p-3 text-sm font-semibold text-foreground">FACTURA</th>
            <th className="text-right p-3 text-sm font-semibold text-foreground">RAPPEL%</th>
            <th className="text-right p-3 text-sm font-semibold text-foreground">RAPPEL€</th>
            {supplier === 'p1' && (
              <th className="text-right p-3 text-sm font-semibold text-foreground">DESC.RAPPEL%</th>
            )}
            <th className="text-right p-3 text-sm font-semibold text-foreground">PRECIO FINAL</th>
          </tr>
        </thead>
        <tbody>
          {tariffs.map((tariff) => {
            const invoice = calculateInvoice(tariff.tariff, tariff.invoiceDiscount);
            const rappelEur = calculateRappelEur(tariff.tariff, tariff.rappel);
            const finalPrice = calculateFinalPrice(invoice, rappelEur, tariff.rappelDiscount);

            return (
              <tr key={tariff.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                <td className="p-3 text-sm text-foreground font-medium">{tariff.productName}</td>
                <td className="p-3 text-right">
                  <Input
                    type="number"
                    step="0.01"
                    value={tariff.tariff}
                    onChange={(e) => onUpdate(tariff.id, 'tariff', parseFloat(e.target.value) || 0)}
                    className="w-24 text-right font-mono text-sm"
                  />
                </td>
                <td className="p-3 text-right">
                  <Input
                    type="number"
                    step="1"
                    value={tariff.invoiceDiscount}
                    onChange={(e) => onUpdate(tariff.id, 'invoiceDiscount', parseFloat(e.target.value) || 0)}
                    className="w-20 text-right font-mono text-sm"
                  />
                </td>
                <td className="p-3 text-right text-sm font-mono text-muted-foreground">{formatEur(invoice)}</td>
                <td className="p-3 text-right">
                  <Input
                    type="number"
                    step="1"
                    value={tariff.rappel}
                    onChange={(e) => onUpdate(tariff.id, 'rappel', parseFloat(e.target.value) || 0)}
                    className="w-20 text-right font-mono text-sm"
                  />
                </td>
                <td className="p-3 text-right text-sm font-mono text-muted-foreground">{formatEur(rappelEur)}</td>
                {supplier === 'p1' && (
                  <td className="p-3 text-right">
                    <Input
                      type="number"
                      step="1"
                      value={tariff.rappelDiscount}
                      onChange={(e) => onUpdate(tariff.id, 'rappelDiscount', parseFloat(e.target.value) || 0)}
                      className="w-20 text-right font-mono text-sm"
                    />
                  </td>
                )}
                <td className="p-3 text-right text-sm font-mono font-semibold text-foreground">{formatEur(finalPrice)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border bg-muted/50">
            <td className="p-3 text-sm font-bold text-foreground">TOTAL</td>
            <td className="p-3 text-right text-sm font-mono font-bold text-foreground">{formatEur(totals.tariff)}</td>
            <td className="p-3"></td>
            <td className="p-3 text-right text-sm font-mono font-bold text-foreground">{formatEur(totals.invoice)}</td>
            <td className="p-3"></td>
            <td className="p-3 text-right text-sm font-mono font-bold text-foreground">{formatEur(totals.rappelEur)}</td>
            {supplier === 'p1' && <td className="p-3"></td>}
            <td className="p-3 text-right text-sm font-mono font-bold text-primary">{formatEur(totals.finalPrice)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function SupplierDataPanel({ p1Tariffs, p2Tariffs, onUpdateTariff }: SupplierDataPanelProps) {
  const p1Total = p1Tariffs.reduce((acc, t) => {
    const invoice = calculateInvoice(t.tariff, t.invoiceDiscount);
    const rappelEur = calculateRappelEur(t.tariff, t.rappel);
    return acc + calculateFinalPrice(invoice, rappelEur, t.rappelDiscount);
  }, 0);

  const p2Total = p2Tariffs.reduce((acc, t) => {
    const invoice = calculateInvoice(t.tariff, t.invoiceDiscount);
    const rappelEur = calculateRappelEur(t.tariff, t.rappel);
    return acc + calculateFinalPrice(invoice, rappelEur, t.rappelDiscount);
  }, 0);

  const isCheaper = p1Total < p2Total ? 'p1' : 'p2';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
    >
      <Card className="overflow-hidden">
        <div className="p-6 border-b border-border">
          <h2 className="text-2xl font-bold text-foreground">Datos de Proveedores</h2>
          <p className="text-sm text-muted-foreground mt-1">Tarifas y descuentos por proveedor</p>
        </div>

        <Tabs defaultValue="p1" className="w-full">
          <div className="px-6 pt-4">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="p1" className="relative">
                Proveedor 1
                {isCheaper === 'p1' && (
                  <Badge variant="default" className="ml-2 bg-success text-success-foreground">
                    Más barato
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="p2" className="relative">
                Proveedor 2
                {isCheaper === 'p2' && (
                  <Badge variant="default" className="ml-2 bg-success text-success-foreground">
                    Más barato
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="p1" className="p-6 pt-4">
            <TariffTable
              tariffs={p1Tariffs}
              supplier="p1"
              onUpdate={(id, field, value) => onUpdateTariff('p1', id, field, value)}
            />
          </TabsContent>

          <TabsContent value="p2" className="p-6 pt-4">
            <TariffTable
              tariffs={p2Tariffs}
              supplier="p2"
              onUpdate={(id, field, value) => onUpdateTariff('p2', id, field, value)}
            />
          </TabsContent>
        </Tabs>

        <div className="px-6 py-4 bg-muted/30 border-t border-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">Comparativa de precios finales</div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Proveedor 1</div>
                <div className="text-lg font-bold font-mono text-foreground">{formatEur(p1Total)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Proveedor 2</div>
                <div className="text-lg font-bold font-mono text-foreground">{formatEur(p2Total)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Ahorro</div>
                <div className="text-lg font-bold font-mono text-success">{formatEur(Math.abs(p1Total - p2Total))}</div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}